// Unified EM2c Simulation Layer
// Keeps the active hex-grid/map and EM2c visual building registry untouched.
// Transplants the advanced gameplay systems (recipes, resource controller,
// load shedding, building states, events, persistence, and gradual restart)
// onto the active em2c-store.

import { useGame, EM2C_RESOURCES, EM2C_RESOURCE_KEYS } from "../em2c-store";
import { EM2C_BUILDINGS } from "../data/em2c-buildings";
import { balanceProduction } from "./resourceAutoBalancer";
import { FACTORY_RECIPES, CONSTRUCTION_YARD_RECIPES, NEW_RESOURCES } from "../data/productionChains";
import { TECHS } from "../data/research";
import { hexDistance } from "../hexgrid";
import {
  runColonyController,
  ALERT_LEVELS,
  COLONY_POLICIES,
  getBuildingPriority,
} from "./ColonyResourceController";
import { maybeTriggerEvent } from "./EventManager";
import {
  sfxInfo,
  sfxWarning,
  sfxStormStart,
} from "../audio";

const SOL_LEN_SEC = 480;
const SYSTEM_SAVE_KEY = "mars_em2c_systems_v2";
const SYSTEM_AUTOSAVE_KEY = "mars_em2c_systems_autosave_v2";
const BASE_AUTOSAVE_KEY = "mars_em2c_autosave_v1";
const RESTART_INTERVAL = 5;

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const dist = (x1, z1, x2, z2) => Math.hypot(x1 - x2, z1 - z2);

const RESOURCE_ALIASES = {
  metal: "refined_metal",
  refined_metal: "refined_metal",
  parts: "machine_parts",
  machine_parts: "machine_parts",
  clean_water: "potable_water",
  potable_water: "potable_water",
  water: "water",
  raw_water: "raw_water",
  raw_food: "raw_food",
  processed_food: "processed_food",
  food: "food",
  raw_ore: "raw_ore",
  metal_ore: "metal_ore",
  sorted_ore: "sorted_ore",
  power: "power",
  oxygen: "oxygen",
  polymers: "polymers",
  credits: "credits",
  research: "research_points",
  research_points: "research_points",
};

const VIRTUAL_OUTPUTS = new Set(["health", "morale", "comfort", "service", "capacity"]);
// Life support uses calibrated mass/volume per simulated second (1 sol = 480s).
const SLOWED_RESOURCES = new Set(["oxygen", "water", "food", "raw_water", "hydrogen"]);
const lifeRate = (key) => SLOWED_RESOURCES.has(key) ? 0.01 : 1;
const UTILITY_KEYS = new Set(["power", "water", "oxygen"]);

const DEFAULT_RESOURCE_MAX = {
  raw_ore: 300,
  sorted_ore: 300,
  structural_components: 150,
  electronic_components: 120,
  utility_components: 120,
  specialized_alloys: 100,
  processed_minerals: 200,
  organic_waste: 200,
  wastewater: 250,
  raw_water: 250,
  potable_water: 250,
  fertilizer: 150,
  nutrients: 150,
  raw_food: 200,
  processed_food: 240,
  building_modules: 100,
  reactor_fuel: 100,
  research_points: 9999,
  rocket_propellant: 150,
  regolith: 300,
  ice: 250,
  glass_feedstock: 150,
  industrial_gases: 200,
  co2: 250,
  nitrogen: 200,
  habitat_module: 50,
  storage_module: 50,
  reactor_module: 30,
  corridor_module: 80,
  tower_module: 40,
  greenhouse_module: 50,
  extraction_module: 50,
  lab_module: 40,
};

let _installed = false;
let _baseSave = null;
let _baseLoad = null;
let _baseReset = null;
let _lastTick = typeof performance !== "undefined" ? performance.now() : Date.now();
let _rafId = null;
let _bgIntervalId = null;
let _visibilityHandlerInstalled = false;
let _autosaveTimer = null;

export function normalizeResourceKey(key) {
  return RESOURCE_ALIASES[key] || key;
}

export function getBuildingRecipesForType(type) {
  return FACTORY_RECIPES[type] || CONSTRUCTION_YARD_RECIPES[type] || [];
}

function humanizeResource(key) {
  return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function inferResourceMeta(key) {
  const source = NEW_RESOURCES[key] || {};
  return {
    label: source.label || humanizeResource(key),
    color: source.color || "#9aa7b3",
    icon: source.icon || "Package",
    max: DEFAULT_RESOURCE_MAX[key] || 500,
  };
}

function collectRecipeResourceKeys() {
  const out = new Set();
  const groups = [FACTORY_RECIPES, CONSTRUCTION_YARD_RECIPES];
  for (const group of groups) {
    for (const recipes of Object.values(group || {})) {
      for (const recipe of recipes || []) {
        for (const key of Object.keys(recipe.inputs || {})) out.add(normalizeResourceKey(key));
        for (const key of Object.keys(recipe.outputs || {})) out.add(normalizeResourceKey(key));
      }
    }
  }
  return out;
}

function ensureExtendedResources(resources) {
  const next = { ...resources };
  const needed = new Set(Object.keys(NEW_RESOURCES).map(normalizeResourceKey));
  for (const key of collectRecipeResourceKeys()) needed.add(key);

  for (const def of Object.values(EM2C_BUILDINGS)) {
    for (const key of Object.keys(def.productionInputs || {})) {
      const normalized = normalizeResourceKey(key);
      if (!VIRTUAL_OUTPUTS.has(normalized)) needed.add(normalized);
    }
    for (const key of Object.keys(def.productionOutputs || {})) {
      const normalized = normalizeResourceKey(key);
      if (!VIRTUAL_OUTPUTS.has(normalized)) needed.add(normalized);
    }
    for (const key of Object.keys(def.storageCapacity || {})) needed.add(normalizeResourceKey(key));
  }

  for (const key of needed) {
    if (VIRTUAL_OUTPUTS.has(key)) continue;
    if (!EM2C_RESOURCES[key]) EM2C_RESOURCES[key] = inferResourceMeta(key);
    if (!EM2C_RESOURCE_KEYS.includes(key)) EM2C_RESOURCE_KEYS.push(key);
    if (!next[key]) {
      const meta = EM2C_RESOURCES[key] || inferResourceMeta(key);
      next[key] = { stored: 0, max: meta.max || DEFAULT_RESOURCE_MAX[key] || 500 };
    }
  }
  return next;
}

function getRecipeForBuilding(state, building) {
  const recipes = getBuildingRecipesForType(building.type);
  if (!recipes.length) return null;
  const selected = state.selectedRecipes?.[building.id];
  return recipes.find((r) => r.id === selected) || null;
}

function isRecipeBuilding(type) {
  return getBuildingRecipesForType(type).length > 0;
}

function getSystemDefaults() {
  return {
    colonyTier: 0,
    selectedRecipes: {},
    colonyPolicy: "BALANCED",
    buildingOverrides: {},
    _colonyAlert: ALERT_LEVELS.NORMAL,
    _colonyControllerState: null,
    _alertStableSince: 0,
    _totalTime: 0,
    _controllerShedIds: [],
    _restartHold: [],
    _lastRestartAt: 0,
    _autoIdleIds: [],
    _autoControlLastAt: 0,
    activeModifiers: {},
    lastEventSol: 0,
    activeEvent: null,
    eventTimeLeft: 0,
  };
}

function persistSystemState(mode = "manual") {
  if (typeof localStorage === "undefined") return;
  const s = useGame.getState();
  const payload = {
    version: 2,
    colonyTier: s.colonyTier || 0,
    selectedRecipes: s.selectedRecipes || {},
    colonyPolicy: s.colonyPolicy || "BALANCED",
    buildingOverrides: s.buildingOverrides || {},
    _alertStableSince: s._alertStableSince || 0,
    _totalTime: s._totalTime || 0,
    _controllerShedIds: s._controllerShedIds || [],
    _restartHold: s._restartHold || [],
    _lastRestartAt: s._lastRestartAt || 0,
    _autoIdleIds: s._autoIdleIds || [],
    _autoControlLastAt: s._autoControlLastAt || 0,
    activeModifiers: s.activeModifiers || {},
    lastEventSol: s.lastEventSol || 0,
    activeEvent: s.activeEvent || null,
    eventTimeLeft: s.eventTimeLeft || 0,
  };
  const key = mode === "auto" ? SYSTEM_AUTOSAVE_KEY : SYSTEM_SAVE_KEY;
  localStorage.setItem(key, JSON.stringify(payload));
}

function restoreSystemState(preferManual = true) {
  if (typeof localStorage === "undefined") return;
  const raw = preferManual
    ? (localStorage.getItem(SYSTEM_SAVE_KEY) || localStorage.getItem(SYSTEM_AUTOSAVE_KEY))
    : (localStorage.getItem(SYSTEM_AUTOSAVE_KEY) || localStorage.getItem(SYSTEM_SAVE_KEY));
  if (!raw) return;
  try {
    const data = JSON.parse(raw);
    useGame.setState({
      ...getSystemDefaults(),
      ...data,
      resources: ensureExtendedResources(useGame.getState().resources),
    });
  } catch (e) {
    console.warn("[MarsColony] Unified system state restore failed", e);
  }
}

export function installUnifiedSystems() {
  if (_installed) return;
  _installed = true;

  const initial = useGame.getState();
  _baseSave = initial.save;
  _baseLoad = initial.load;
  _baseReset = initial.reset;

  const defaults = getSystemDefaults();
  useGame.setState({
    ...defaults,
    colonyTier: initial.colonyTier ?? defaults.colonyTier,
    selectedRecipes: initial.selectedRecipes || defaults.selectedRecipes,
    colonyPolicy: initial.colonyPolicy || defaults.colonyPolicy,
    buildingOverrides: initial.buildingOverrides || defaults.buildingOverrides,
    resources: ensureExtendedResources(initial.resources),

    setRecipe: (buildingId, recipeId) => {
      const s = useGame.getState();
      const b = s.buildings.find((x) => x.id === buildingId);
      if (!b) return;
      const recipes = getBuildingRecipesForType(b.type);
      if (recipeId && !recipes.some((r) => r.id === recipeId)) return;
      useGame.setState({
        selectedRecipes: { ...s.selectedRecipes, [buildingId]: recipeId || null },
        buildings: s.buildings.map((x) => x.id === buildingId ? { ...x, recipeProgress: 0 } : x),
      });
      s.pushNotification?.({
        level: "info",
        text: recipeId
          ? `${EM2C_BUILDINGS[b.type]?.name || b.type}: production recipe selected`
          : `${EM2C_BUILDINGS[b.type]?.name || b.type}: production set to standby`,
      });
    },

    getBuildingRecipes: (buildingIdOrType) => {
      const s = useGame.getState();
      const b = s.buildings.find((x) => x.id === buildingIdOrType);
      return getBuildingRecipesForType(b?.type || buildingIdOrType);
    },

    setColonyPolicy: (policy) => {
      if (!COLONY_POLICIES[policy]) return;
      useGame.setState({ colonyPolicy: policy });
      useGame.getState().pushNotification?.({ level: "info", text: `Colony policy: ${COLONY_POLICIES[policy].label}` });
    },

    setBuildingPriority: (buildingId, priority) => {
      const p = clamp(Number(priority), 0, 5);
      const s = useGame.getState();
      useGame.setState({ buildingOverrides: { ...s.buildingOverrides, [buildingId]: p } });
    },

    resetBuildingPriority: (buildingId) => {
      const s = useGame.getState();
      const next = { ...s.buildingOverrides };
      delete next[buildingId];
      useGame.setState({ buildingOverrides: next });
    },

    save: (mode = "manual") => {
      _baseSave?.(mode);
      try { persistSystemState(mode); } catch (e) { console.warn("[MarsColony] System save failed", e); }
    },

    load: () => {
      _baseLoad?.();
      useGame.setState({ resources: ensureExtendedResources(useGame.getState().resources) });
      restoreSystemState(true);
    },

    reset: () => {
      _baseReset?.();
      useGame.setState({
        ...getSystemDefaults(),
        resources: ensureExtendedResources(useGame.getState().resources),
      });
    },
  });
}

function getCaps(state, buildings, resources) {
  const caps = {};
  for (const [key, resource] of Object.entries(resources)) {
    caps[key] = EM2C_RESOURCES[key]?.max || DEFAULT_RESOURCE_MAX[key] || resource.max || 500;
  }

  const completed = state.research?.completed || [];
  const storageMult = completed.includes("eng_storage2") ? 1.5 : 1;
  const batteryMult = completed.includes("eng_battery1") ? 1.75 : 1;
  const fuelStorageMult = completed.includes("aero_fuel1") ? 1.5 : 1;

  for (const b of buildings) {
    if (b.progress < 1 || b.active === false) continue;
    const def = EM2C_BUILDINGS[b.type];
    if (!def?.storageCapacity) continue;
    for (const [rawKey, value] of Object.entries(def.storageCapacity)) {
      const key = normalizeResourceKey(rawKey);
      if (!resources[key]) continue;
      let mult = storageMult;
      if (key === "power" && (b.type.includes("battery") || def.category === "power")) mult *= batteryMult;
      if ((key === "rocket_propellant" || key === "reactor_fuel") && (b.type.includes("fuel") || b.type.includes("propellant"))) mult *= fuelStorageMult;
      caps[key] = (caps[key] || 0) + value * mult;
    }
  }
  return caps;
}

function getPhysicalOutputs(def, recipe) {
  const source = recipe?.outputs || def?.productionOutputs || {};
  const outputs = [];
  for (const [rawKey, rate] of Object.entries(source)) {
    if (!rate) continue;
    if (VIRTUAL_OUTPUTS.has(rawKey)) continue;
    const key = normalizeResourceKey(rawKey);
    if (key === "power") continue;
    // Hydrogen is a secondary electrolysis output. A full hydrogen tank must
    // never stop oxygen generation for a living colony; excess is pressure-relieved.
    if (def?.id === "oxygen_gen" && key === "hydrogen") continue;
    outputs.push([key, rate]);
  }
  return outputs;
}

function hasOutputRoom(resources, caps, outputs, fullAmounts = false) {
  for (const [key, amount] of outputs) {
    const res = resources[key];
    if (!res) continue;
    const max = caps[key] ?? res.max ?? 500;
    const room = max - res.stored;
    if (fullAmounts ? room + 1e-6 < amount : room <= 1e-6) return false;
  }
  return true;
}

function getMaterialInputs(def, recipe) {
  const source = recipe?.inputs || def?.productionInputs || {};
  const inputs = [];
  for (const [rawKey, amount] of Object.entries(source)) {
    const key = normalizeResourceKey(rawKey);
    if (UTILITY_KEYS.has(key)) continue;
    inputs.push([key, amount]);
  }
  return inputs;
}

function hasMaterialInputs(resources, inputs, dt = 1, recipeMode = false) {
  for (const [key, amount] of inputs) {
    const need = recipeMode ? amount : amount * dt;
    if ((resources[key]?.stored || 0) + 1e-9 < need) return false;
  }
  return true;
}

function allocateWorkers(state, buildings, preliminaryBlocks) {
  let available = state.colonists?.length || 0;
  const assignments = {};
  const candidates = buildings
    .filter((b) => b.progress >= 1 && b.active !== false && !preliminaryBlocks[b.id])
    .filter((b) => (EM2C_BUILDINGS[b.type]?.workersRequired || 0) > 0)
    .sort((a, b) => {
      const pa = state.buildingOverrides?.[a.id] ?? getBuildingPriority(a.type);
      const pb = state.buildingOverrides?.[b.id] ?? getBuildingPriority(b.type);
      return pa - pb;
    });

  for (const b of candidates) {
    const required = EM2C_BUILDINGS[b.type]?.workersRequired || 0;
    const assigned = Math.min(required, available);
    assignments[b.id] = assigned;
    available -= assigned;
  }
  return assignments;
}

function preliminaryBuildingBlocks(state, buildings, resources, caps) {
  const blocks = {};
  for (const b of buildings) {
    const def = EM2C_BUILDINGS[b.type];
    if (!def) continue;
    if (b.progress < 1) { blocks[b.id] = { state: "under_construction", reason: "Constructing" }; continue; }
    if (b.active === false) { blocks[b.id] = { state: "disabled", reason: "Player disabled" }; continue; }
    if ((b.condition ?? 1) <= 0.3) { blocks[b.id] = { state: "damaged", reason: "Damaged" }; continue; }

    const recipe = getRecipeForBuilding(state, b);
    if (isRecipeBuilding(b.type) && !recipe) {
      blocks[b.id] = { state: "standby", reason: "No production recipe selected" };
      continue;
    }

    const outputs = getPhysicalOutputs(def, recipe);
    if (outputs.length && !hasOutputRoom(resources, caps, outputs, !!recipe)) {
      blocks[b.id] = { state: "output_full", reason: "Output storage full" };
      continue;
    }

    const materialInputs = getMaterialInputs(def, recipe);
    if (materialInputs.length && !hasMaterialInputs(resources, materialInputs, 0.1, !!recipe)) {
      blocks[b.id] = { state: "input_shortage", reason: "Required input unavailable" };
      continue;
    }
  }
  return blocks;
}

function controllerResourceView(resources) {
  const view = {};
  for (const [key, value] of Object.entries(resources)) {
    if (["power", "water", "oxygen"].includes(key)) view[key] = value;
    else view[key] = { ...value, stored: Math.max(value?.stored || 0, 1000) };
  }
  view.metal = { ...(resources.refined_metal || resources.metal || { max: 1000 }), stored: 1000 };
  view.parts = { ...(resources.machine_parts || resources.parts || { max: 1000 }), stored: 1000 };
  return view;
}

function reconcileLoadShedding(state, controllerState, buildings, totalTime) {
  const currentRequired = new Set(controllerState.shedBuildings || []);
  const previousRequired = new Set(state._controllerShedIds || []);
  const hold = new Set(state._restartHold || []);
  const liveIds = new Set(buildings.map((b) => b.id));

  for (const id of [...hold]) if (!liveIds.has(id)) hold.delete(id);
  for (const id of currentRequired) hold.add(id);
  for (const id of previousRequired) {
    if (!currentRequired.has(id) && liveIds.has(id)) hold.add(id);
  }

  let lastRestartAt = state._lastRestartAt || 0;
  const releasable = [...hold]
    .filter((id) => !currentRequired.has(id))
    .map((id) => buildings.find((b) => b.id === id))
    .filter(Boolean)
    .sort((a, b) => {
      const pa = state.buildingOverrides?.[a.id] ?? getBuildingPriority(a.type);
      const pb = state.buildingOverrides?.[b.id] ?? getBuildingPriority(b.type);
      return pa - pb;
    });

  if (releasable.length && totalTime - lastRestartAt >= RESTART_INTERVAL) {
    hold.delete(releasable[0].id);
    lastRestartAt = totalTime;
  }

  return { finalShed: new Set([...currentRequired, ...hold]), hold, lastRestartAt };
}

function techMultipliers(state, building, daylight) {
  const completed = state.research?.completed || [];
  const activeModifiers = state.activeModifiers || {};
  const isSolar = building.type === "solar_station" || building.type.includes("solar");

  let solarMult = 1;
  if (isSolar) {
    let stormFactor = state.storm?.active ? 0.15 : 1;
    if (state.storm?.active && completed.includes("aero_storm")) stormFactor = 0.5;
    solarMult = daylight * stormFactor * (completed.includes("eng_solar2") ? 1.5 : 1);
    if (activeModifiers.solar_output) solarMult *= activeModifiers.solar_output;
  }

  let reactorBoost = 1;
  if ((building.type.includes("nuclear") || building.type.includes("smr") || building.type.includes("fission")) && completed.includes("eng_reactor1")) reactorBoost = 1.4;
  const fusionBoost = completed.includes("eng_fusion1") ? (building.type.includes("fusion") ? 2 : 1.25) : 1;
  const heatSinkBoost = completed.includes("eng_heat1") ? 1.25 : 1;

  let waterBoost = 1;
  if ((building.type.includes("water") || building.type.includes("extractor") || building.type.includes("well")) && completed.includes("bio_water2")) waterBoost *= 1.5;
  if ((building.type.includes("recycle") || building.type.includes("recycler") || building.type.includes("waste")) && completed.includes("bio_recycle1")) waterBoost *= 1.25;

  let o2Boost = 1;
  if ((building.type.includes("oxygen") || building.type.includes("o2")) && completed.includes("aero_oxygen2")) o2Boost *= 1.5;
  if ((building.type.includes("atmo") || building.type.includes("scrubber")) && completed.includes("aero_atmo1")) o2Boost *= 1.6;
  if (completed.includes("aero_terra1")) o2Boost *= 2;

  let foodMult = 1;
  if (building.type === "greenhouse" || building.type.includes("agri") || building.type.includes("farm") || building.type.includes("crop")) {
    if (completed.includes("bio_hydro2")) foodMult *= 2;
    if (completed.includes("bio_soil1")) foodMult *= 1.3;
    if (completed.includes("bio_engineering1")) foodMult *= 1.8;
  }
  if (completed.includes("bio_ecosystem1")) foodMult *= 1.2;

  return {
    isSolar,
    solarMult,
    reactorBoost,
    fusionBoost,
    heatSinkBoost,
    waterBoost,
    o2Boost,
    foodMult,
    mfgMult: completed.includes("eng_mfg1") ? 1.35 : 1,
    productionEventMult: activeModifiers.production || 1,
  };
}

function outputMultiplierForKey(key, multipliers) {
  if (key === "power") return multipliers.isSolar
    ? multipliers.solarMult
    : multipliers.reactorBoost * multipliers.fusionBoost * multipliers.heatSinkBoost;
  if (key === "water" || key === "raw_water" || key === "potable_water") return multipliers.waterBoost * multipliers.productionEventMult;
  if (key === "oxygen") return multipliers.o2Boost * multipliers.productionEventMult;
  if (key === "food" || key === "raw_food" || key === "processed_food") return multipliers.foodMult * multipliers.productionEventMult;
  if (["machine_parts", "components", "building_modules", "polymers", "structural_components", "electronic_components", "utility_components", "specialized_alloys"].includes(key)) {
    return multipliers.mfgMult * multipliers.productionEventMult;
  }
  return multipliers.productionEventMult;
}

function applyEvent(stateAfterTick, resources, buildings, research, activeModifiers, eventTimeLeft, activeEvent, dt) {
  let nextResources = resources;
  let nextBuildings = buildings;
  let nextResearch = research;
  let nextModifiers = { ...(activeModifiers || {}) };
  let nextEventTime = eventTimeLeft || 0;
  let nextActiveEvent = activeEvent || null;
  let lastEventSol = stateAfterTick.lastEventSol || 0;

  if (nextEventTime > 0) {
    nextEventTime = Math.max(0, nextEventTime - dt);
    if (nextEventTime <= 0) {
      nextModifiers = {};
      nextActiveEvent = null;
    }
  }

  if (!nextActiveEvent) {
    const result = maybeTriggerEvent({ ...stateAfterTick, resources: nextResources, buildings: nextBuildings, research: nextResearch }, dt);
    if (result) {
      const { event, effect = {} } = result;
      lastEventSol = stateAfterTick.sol;
      const level = effect.severity === "bad" || event.severity === "bad" ? "warning" : "info";
      stateAfterTick.pushNotification?.({ level, text: effect.text || event.description || event.name });
      if (level === "warning") sfxWarning(); else sfxInfo();

      if (effect.changes) {
        const copy = { ...nextResources };
        for (const [rawKey, delta] of Object.entries(effect.changes)) {
          const key = normalizeResourceKey(rawKey);
          const r = copy[key];
          if (!r) continue;
          copy[key] = { ...r, stored: clamp(r.stored + delta, 0, r.max) };
        }
        nextResources = copy;
      }
      if (effect.buildings) nextBuildings = effect.buildings;
      if (effect.research) nextResearch = effect.research;
      if (effect.modifier?.type) {
        nextModifiers[effect.modifier.type] = effect.modifier.factor ?? 1;
        nextEventTime = effect.modifier.duration || effect.duration || 0;
        nextActiveEvent = { id: event.id, name: event.name, severity: event.severity };
      }
    }
  }

  return {
    resources: nextResources,
    buildings: nextBuildings,
    research: nextResearch,
    activeModifiers: nextModifiers,
    eventTimeLeft: nextEventTime,
    activeEvent: nextActiveEvent,
    lastEventSol,
  };
}

export function tick(dtReal) {
  installUnifiedSystems();
  const s = useGame.getState();
  if (s.isPaused || s.victory || s.defeat) return;

  const dt = dtReal * s.gameSpeed;
  const totalTime = (s._totalTime || 0) + dt;
  const completed = s.research?.completed || [];
  const timeFrac = dt / SOL_LEN_SEC;
  let newTime = s.time + timeFrac;
  let newSol = s.sol;
  let survivedSols = s.survivedSols || 0;
  let crossedSol = false;

  if (newTime >= 1) {
    newTime -= 1;
    newSol += 1;
    crossedSol = true;
    const lifeOk = s.resources.oxygen.stored > 0 && s.resources.water.stored > 0 && (s.colonists.length === 0 || s.resources.food.stored > 0);
    if (s.colonists.length >= 12 && lifeOk) survivedSols += 1;
    else survivedSols = 0;
  }

  const daylight = clamp(Math.sin(newTime * Math.PI) * 1.4, 0, 1);
  let resources = ensureExtendedResources(Object.fromEntries(Object.entries(s.resources).map(([k, v]) => [k, { ...v }])));
  let buildings = s.buildings.map((b) => ({ ...b }));
  const deposits = s.deposits.map((d) => ({ ...d }));

  if (crossedSol && completed.includes("aero_orbital1")) {
    const bonus = { refined_metal: 40, machine_parts: 20, polymers: 20, food: 30, credits: 50 };
    for (const [key, amount] of Object.entries(bonus)) {
      const r = resources[key];
      if (r) resources[key] = { ...r, stored: Math.min(r.max, r.stored + amount) };
    }
    s.pushNotification?.({ level: "info", text: `Sol ${newSol}: Orbital supply drop received!` });
  }

  const caps = getCaps(s, buildings, resources);
  for (const [key, cap] of Object.entries(caps)) {
    if (resources[key]) resources[key] = { ...resources[key], max: cap, stored: Math.min(resources[key].stored, cap) };
  }

  for (const b of buildings) {
    const def = EM2C_BUILDINGS[b.type];
    if (!def || b.progress >= 1) continue;
    const droneHere = s.drones.some((d) => d.task === "build" && d.target === b.id && dist(d.x, d.z, b.x, b.z) < 1.5);
    const droneBuildMult = completed.includes("eng_drones2") ? 2 : 1;
    const nanobotsMult = completed.includes("eng_nanobots1") ? 2.5 : 1;
    b.progress = clamp(b.progress + dt * (droneHere ? 0.15 * droneBuildMult : 0.04) * nanobotsMult * (def.buildTime > 0 ? 8 / def.buildTime : 0.5), 0, 1);
    b.state = b.progress >= 1 ? "operational" : "under_construction";
  }

  const autoBalance = balanceProduction(s, buildings, resources, totalTime);
  const preBlocks = preliminaryBuildingBlocks(s, buildings, resources, caps);
  for (const b of buildings) {
    if (autoBalance.idle.has(b.id) && !preBlocks[b.id]) {
      preBlocks[b.id] = { state: "auto_idle", reason: "Automatic production balancing" };
    }
  }
  const workerAssignments = allocateWorkers(s, buildings, preBlocks);
  for (const b of buildings) {
    const def = EM2C_BUILDINGS[b.type];
    if (!def) continue;
    const required = def.workersRequired || 0;
    if (required > 0 && !preBlocks[b.id]) {
      b.workers = workerAssignments[b.id] || 0;
      if (b.workers < required) preBlocks[b.id] = { state: "no_workers", reason: "Insufficient workers" };
    } else if (required === 0) b.workers = 0;
  }

  const controllerEligible = buildings.filter((b) => !preBlocks[b.id] || b.progress < 1);
  const controllerStateRaw = runColonyController({
    ...s,
    time: newTime,
    resources: controllerResourceView(resources),
    buildings: controllerEligible,
    _totalTime: totalTime,
    colonyPolicy: s.colonyPolicy || "BALANCED",
    buildingOverrides: s.buildingOverrides || {},
  }, dt);

  let alertStableSince = s._alertStableSince || 0;
  if (controllerStateRaw.alertId !== s._colonyAlert?.id) {
    alertStableSince = totalTime;
    if (controllerStateRaw.alert.priority > (s._colonyAlert?.priority || 0)) {
      s.pushNotification?.({
        level: controllerStateRaw.alert.priority >= 3 ? "critical" : "warning",
        text: `Colony Alert: ${controllerStateRaw.alert.label}`,
      });
    }
  }

  const shedding = reconcileLoadShedding(s, controllerStateRaw, buildings, totalTime);
  const finalShed = new Set([...shedding.finalShed, ...autoBalance.idle]);
  const available = {};
  const deltas = {};
  for (const key of Object.keys(resources)) {
    available[key] = resources[key]?.stored || 0;
    deltas[key] = 0;
  }

  let actualPowerProduction = 0;
  let actualPowerConsumption = 0;
  let moraleRate = 0;

  for (const b of buildings) {
    const def = EM2C_BUILDINGS[b.type];
    if (!def || b.progress < 1) continue;

    const preliminary = preBlocks[b.id];
    if (preliminary) {
      b.state = preliminary.state;
      b.controllerStatus = preliminary.state;
      b.controllerReason = preliminary.reason;
      b.controllerShed = false;
      continue;
    }

    if (finalShed.has(b.id)) {
      b.state = "power_shed";
      b.controllerStatus = "power_shed";
      b.controllerReason = "Automatic load shedding";
      b.controllerShed = true;
      continue;
    }

    b.controllerShed = false;
    const ctrlStatus = controllerStateRaw.buildingStatuses?.[b.id];
    b.reducedCapacity = ctrlStatus?.reducedCapacity || null;
    const capMult = b.reducedCapacity || 1;
    const condition = b.condition ?? 1;
    const recipe = getRecipeForBuilding(s, b);
    const multipliers = techMultipliers({ ...s, time: newTime }, b, daylight);

    if (recipe) {
      const fullOutputs = Object.entries(recipe.outputs || {}).map(([rawKey, amount]) => [normalizeResourceKey(rawKey), amount]);
      if (!hasOutputRoom(Object.fromEntries(Object.keys(resources).map((k) => [k, { ...resources[k], stored: available[k] }])), caps, fullOutputs, true)) {
        b.state = "output_full";
        b.controllerStatus = "output_full";
        b.controllerReason = "Output storage full";
        continue;
      }

      const utilityCosts = {
        power: (def.powerConsumption || 0) * dt * capMult,
        water: (def.waterConsumption || 0) * dt * capMult,
        oxygen: (def.oxygenConsumption || 0) * dt * capMult,
      };
      let utilityBlocked = null;
      for (const [key, cost] of Object.entries(utilityCosts)) {
        if (cost > 0 && available[key] + 1e-9 < cost) { utilityBlocked = key; break; }
      }
      if (utilityBlocked) {
        b.state = utilityBlocked === "power" ? "no_power" : utilityBlocked === "water" ? "no_water" : "no_oxygen";
        b.controllerStatus = b.state;
        b.controllerReason = `No ${utilityBlocked}`;
        continue;
      }

      const materialInputs = getMaterialInputs(def, recipe);
      if (!hasMaterialInputs(Object.fromEntries(Object.keys(resources).map((k) => [k, { stored: available[k] }])), materialInputs, 1, true)) {
        b.state = "input_shortage";
        b.controllerStatus = "input_shortage";
        b.controllerReason = "Required input unavailable";
        continue;
      }

      for (const [key, cost] of Object.entries(utilityCosts)) {
        if (cost <= 0) continue;
        available[key] -= cost;
        deltas[key] -= cost;
        if (key === "power") actualPowerConsumption += cost / Math.max(dt, 1e-6);
      }

      const recipeSpeed = multipliers.mfgMult * multipliers.productionEventMult * condition * capMult;
      let progress = (b.recipeProgress || 0) + dt * recipeSpeed;
      if (progress >= recipe.time) {
        let enough = true;
        for (const [rawKey, amount] of Object.entries(recipe.inputs || {})) {
          const key = normalizeResourceKey(rawKey);
          if ((available[key] || 0) + 1e-9 < amount) { enough = false; break; }
        }
        if (!enough) {
          b.state = "input_shortage";
          b.controllerStatus = "input_shortage";
          b.controllerReason = "Required input unavailable";
          b.recipeProgress = Math.min(progress, recipe.time - 0.001);
          continue;
        }

        let room = true;
        for (const [rawKey, amount] of Object.entries(recipe.outputs || {})) {
          const key = normalizeResourceKey(rawKey);
          if (key === "power" || VIRTUAL_OUTPUTS.has(rawKey)) continue;
          const max = caps[key] ?? resources[key]?.max ?? 500;
          if (max - (available[key] || 0) + 1e-9 < amount) { room = false; break; }
        }
        if (!room) {
          b.state = "output_full";
          b.controllerStatus = "output_full";
          b.controllerReason = "Output storage full";
          b.recipeProgress = Math.min(progress, recipe.time - 0.001);
          continue;
        }

        for (const [rawKey, amount] of Object.entries(recipe.inputs || {})) {
          const key = normalizeResourceKey(rawKey);
          available[key] -= amount;
          deltas[key] = (deltas[key] || 0) - amount;
        }
        for (const [rawKey, amount] of Object.entries(recipe.outputs || {})) {
          if (VIRTUAL_OUTPUTS.has(rawKey)) continue;
          const key = normalizeResourceKey(rawKey);
          available[key] = (available[key] || 0) + amount;
          deltas[key] = (deltas[key] || 0) + amount;
        }
        progress -= recipe.time;
      }
      b.recipeProgress = progress;
      b.state = "operational";
      b.controllerStatus = "operational";
      b.controllerReason = null;
    } else {
      if (def.productionOutputs?.morale) moraleRate += def.productionOutputs.morale * condition * capMult;
      const outputs = Object.entries(def.productionOutputs || {})
        .filter(([rawKey, rate]) => rate && !VIRTUAL_OUTPUTS.has(rawKey))
        .map(([rawKey, rate]) => [normalizeResourceKey(rawKey), rate, rawKey]);

      let outputFull = false;
      for (const [key] of outputs.filter(([key]) => key !== "power" && !(b.type === "oxygen_gen" && key === "hydrogen"))) {
        const max = caps[key] ?? resources[key]?.max ?? 500;
        if (max - (available[key] || 0) <= 1e-6) { outputFull = true; break; }
      }
      if (outputFull) {
        b.state = "output_full";
        b.controllerStatus = "output_full";
        b.controllerReason = "Output storage full";
        continue;
      }

      const costs = {};
      for (const [rawKey, rate] of Object.entries(def.productionInputs || {})) {
        const key = normalizeResourceKey(rawKey);
        costs[key] = (costs[key] || 0) + rate * dt * capMult * lifeRate(key);
      }
      if (def.powerConsumption) costs.power = (costs.power || 0) + def.powerConsumption * dt * capMult;
      if (def.waterConsumption) costs.water = (costs.water || 0) + def.waterConsumption * dt * capMult;
      if (def.oxygenConsumption) costs.oxygen = (costs.oxygen || 0) + def.oxygenConsumption * dt * capMult;

      let blocked = null;
      for (const [key, cost] of Object.entries(costs)) {
        if ((available[key] || 0) + 1e-9 < cost) { blocked = key; break; }
      }
      if (blocked) {
        b.state = blocked === "power" ? "no_power" : blocked === "water" ? "no_water" : blocked === "oxygen" ? "no_oxygen" : "input_shortage";
        b.controllerStatus = b.state;
        b.controllerReason = blocked === "power" ? "No power" : blocked === "water" ? "No water" : blocked === "oxygen" ? "No oxygen" : "Input shortage";
        continue;
      }

      for (const [key, cost] of Object.entries(costs)) {
        available[key] = (available[key] || 0) - cost;
        deltas[key] = (deltas[key] || 0) - cost;
        if (key === "power") actualPowerConsumption += cost / Math.max(dt, 1e-6);
      }

      for (const [key, rate, rawKey] of outputs) {
        if (VIRTUAL_OUTPUTS.has(rawKey)) continue;
        const amount = rate * dt * condition * capMult * outputMultiplierForKey(key, multipliers) * lifeRate(key);
        if (key === "power") {
          available[key] = (available[key] || 0) + amount;
          deltas[key] = (deltas[key] || 0) + amount;
          actualPowerProduction += amount / Math.max(dt, 1e-6);
        } else {
          const max = caps[key] ?? resources[key]?.max ?? 500;
          const room = Math.max(0, max - (available[key] || 0));
          const applied = Math.min(amount, room);
          available[key] = (available[key] || 0) + applied;
          deltas[key] = (deltas[key] || 0) + applied;
        }
      }

      b.state = "operational";
      b.controllerStatus = "operational";
      b.controllerReason = null;
    }

    if (["water_extractor", "mining_outpost", "ice_processor"].includes(b.type)) {
      const depKind = b.type === "mining_outpost" ? "metal" : "water";
      const dep = deposits.find((d) => d.kind === depKind && (d.hexes || [{ q: d.q, r: d.r }]).some((h) => hexDistance(h.q, h.r, b.q, b.r) <= 1));
      if (dep) {
        const depleteRate = completed.includes("bio_water2") ? 0.025 : 0.05;
        dep.remaining = Math.max(0, dep.remaining - dt * depleteRate);
      }
    }

    const repairFactor = completed.includes("eng_repair1") ? 0.5 : 1;
    const autoRepair = completed.includes("eng_repair1") ? dt * 0.0002 : 0;
    b.condition = clamp((b.condition ?? 1) - dt * 0.0004 * repairFactor + autoRepair, 0.3, 1);
  }

  const nCol = s.colonists.length;
  if (nCol > 0) {
    const foodConsMult = completed.includes("bio_foodproc1") ? 0.75 : 1;
    let lifeSupportMult = 1;
    if (completed.includes("bio_life1")) lifeSupportMult *= 0.85;
    if (completed.includes("bio_ecosystem1")) lifeSupportMult *= 0.75;
    deltas.oxygen = (deltas.oxygen || 0) - nCol * 0.4 * lifeSupportMult * dt * lifeRate("oxygen");
    deltas.water = (deltas.water || 0) - nCol * 0.3 * lifeSupportMult * dt * lifeRate("water");
    deltas.food = (deltas.food || 0) - nCol * 0.15 * foodConsMult * dt * lifeRate("food");
  }

  for (const key of Object.keys(resources)) {
    const delta = deltas[key] || 0;
    const cur = resources[key];
    const max = caps[key] ?? cur.max ?? 500;
    resources[key] = { ...cur, max, stored: clamp(cur.stored + delta, 0, max) };
  }

  if (resources.credits) {
    const gain = dt * 0.4;
    resources.credits = { ...resources.credits, stored: clamp(resources.credits.stored + gain, 0, resources.credits.max) };
    deltas.credits = (deltas.credits || 0) + gain;
  }

  const netRates = {};
  for (const key of Object.keys(resources)) netRates[key] = dtReal > 0 ? (deltas[key] || 0) / dtReal : 0;

  const medModules = buildings.filter((b) => {
    const def = EM2C_BUILDINGS[b.type];
    return def && (def.category === "medical" || b.type.includes("medical") || b.type.includes("med")) && b.progress >= 1 && b.active !== false && !b.controllerShed;
  });
  const completedBuildings = buildings.filter((b) => b.progress >= 1);
  const colonists = s.colonists.map((c) => {
    let h = c.health;
    let morale = c.morale ?? 0.8;
    const medMult = completed.includes("bio_medical") ? 0.5 : 1;
    if (resources.oxygen.stored <= 0.5) h -= dt * 0.02 * medMult;
    if (resources.water.stored <= 0.5) h -= dt * 0.015 * medMult;
    if (resources.food.stored <= 0.5) h -= dt * 0.008 * medMult;
    else h = clamp(h + dt * 0.002, 0, 1);

    if (h < 1 && medModules.length > 0 && resources.power.stored > 1) {
      const healRate = medModules.reduce((acc, b) => acc + (EM2C_BUILDINGS[b.type]?.healRate || 0.01) * (b.condition ?? 1), 0);
      h = clamp(h + dt * healRate * (completed.includes("bio_health1") ? 1.5 : 1), 0, 1);
    }

    morale = clamp(morale + moraleRate * dt * 0.001 - (h < 0.5 ? dt * 0.001 : 0), 0, 1);
    let { x, z, tx, tz, moveTimer } = c;
    if (!tx && !tz) { tx = 0; tz = 8; }
    if (moveTimer === undefined) moveTimer = 0;
    moveTimer -= dt;
    const dx = tx - x;
    const dz = tz - z;
    const dd = Math.hypot(dx, dz);
    const moveSpeed = 2.5 * dt;
    if (dd < 1 || moveTimer <= 0) {
      if (completedBuildings.length > 0 && Math.random() > 0.3) {
        const target = completedBuildings[Math.floor(Math.random() * completedBuildings.length)];
        tx = (target.x || 0) + (Math.random() - 0.5) * 4;
        tz = (target.z || 0) + (Math.random() - 0.5) * 4;
      } else {
        tx = (Math.random() - 0.5) * 30;
        tz = (Math.random() - 0.5) * 30;
      }
      moveTimer = 5 + Math.random() * 10;
    } else if (dd > 0.1) {
      x += (dx / dd) * Math.min(moveSpeed, dd);
      z += (dz / dd) * Math.min(moveSpeed, dd);
    }
    return { ...c, health: clamp(h, 0, 1), morale, x, z, tx, tz, moveTimer };
  });

  const droneEventMult = s.activeModifiers?.drone_speed || 1;
  const drones = s.drones.map((d) => {
    let { x, z, tx, tz, task, target } = d;
    if (task === "idle") {
      const site = buildings.find((b) => b.progress < 1);
      if (site) { task = "build"; target = site.id; tx = site.x || 0; tz = site.z || 0; }
    } else if (task === "build") {
      const site = buildings.find((b) => b.id === target);
      if (!site || site.progress >= 1) { task = "idle"; target = null; tx = 0; tz = 0; }
      else { tx = site.x || 0; tz = site.z || 0; }
    }
    const dx = tx - x;
    const dz = tz - z;
    const dd = Math.hypot(dx, dz);
    const speed = 6 * droneEventMult * dt;
    if (dd > 0.1) { x += (dx / dd) * Math.min(speed, dd); z += (dz / dd) * Math.min(speed, dd); }
    return { ...d, x, z, tx, tz, task, target };
  });

  let storm = { ...s.storm };
  storm.next -= timeFrac;
  const warningThreshold = completed.includes("aero_sat1") ? 0.3 : 0.15;
  if (!storm.active && !storm.warning && storm.next < warningThreshold) {
    storm.warning = true;
    s.pushNotification?.({ level: "warning", text: "Dust storm approaching" });
  }
  if (!storm.active && storm.next < 0) {
    storm.active = true;
    storm.warning = false;
    storm.timeLeft = 30 + Math.random() * 20;
    s.pushNotification?.({ level: "critical", text: "Dust storm hit — solar output crippled" });
    sfxStormStart();
  }
  if (storm.active) {
    storm.timeLeft -= dt;
    if (storm.timeLeft <= 0) {
      storm.active = false;
      storm.next = 3 + Math.random() * 4;
      s.pushNotification?.({ level: "info", text: "Dust storm has passed" });
    }
  }

  let cargo = { ...s.cargo };
  let cargoJustArrived = false;
  if (cargo.active) {
    const commSpeed = completed.includes("aero_comm1") ? 1.25 : 1;
    cargo.remaining -= dt * commSpeed;
    if (cargo.remaining <= 0) { cargo.remaining = 0; cargoJustArrived = true; }
  }

  let research = s.research;
  if (research.active) {
    const remaining = research.active.remaining - dt;
    if (remaining <= 0) {
      const completedTech = TECHS.find((t) => t.id === research.active.id);
      research = { completed: [...research.completed, research.active.id], active: null };
      sfxInfo();
      s.pushNotification?.({ level: "info", text: `Research complete: ${completedTech?.name}` });
    } else research = { ...research, active: { ...research.active, remaining } };
  }

  const colonyTier = Math.max(0, ...research.completed.map((id) => TECHS.find((t) => t.id === id)?.tier || 0));
  const eventApplied = applyEvent(
    { ...s, sol: newSol, time: newTime, pushNotification: s.pushNotification },
    resources,
    buildings,
    research,
    s.activeModifiers,
    s.eventTimeLeft,
    s.activeEvent,
    dt
  );
  resources = eventApplied.resources;
  buildings = eventApplied.buildings;
  research = eventApplied.research;

  let victory = s.victory;
  let defeat = s.defeat;
  let defeatReason = s.defeatReason;
  if (survivedSols >= 10) victory = true;
  if (s.colonists.length > 0 && colonists.every((c) => c.health <= 0)) { defeat = true; defeatReason = "All colonists have perished."; }
  if (s.colonists.length > 0 && resources.oxygen.stored <= 0) { defeat = true; defeatReason = "Oxygen supply depleted."; }
  if (s.colonists.length > 0 && resources.water.stored <= 0) { defeat = true; defeatReason = "Water supply depleted."; }

  const shedCount = {};
  for (const id of finalShed) {
    const b = buildings.find((x) => x.id === id);
    if (!b) continue;
    const p = s.buildingOverrides?.[id] ?? getBuildingPriority(b.type);
    shedCount[p] = (shedCount[p] || 0) + 1;
  }
  const controllerState = {
    ...controllerStateRaw,
    powerProduction: actualPowerProduction,
    powerConsumption: actualPowerConsumption,
    netPower: actualPowerProduction - actualPowerConsumption,
    shedBuildings: finalShed,
    shedCount,
    activeCount: buildings.filter((b) => b.progress >= 1 && b.active !== false && !finalShed.has(b.id)).length,
    totalCount: buildings.filter((b) => b.progress >= 1 && b.active !== false).length,
  };

  useGame.setState({
    sol: newSol,
    time: newTime,
    resources,
    buildings,
    deposits,
    drones,
    colonists,
    storm,
    cargo,
    research,
    survivedSols,
    victory,
    defeat,
    defeatReason,
    netRates,
    colonyTier,
    _colonyAlert: controllerStateRaw.alert,
    _colonyControllerState: controllerState,
    _alertStableSince: alertStableSince,
    _totalTime: totalTime,
    _controllerShedIds: [...new Set(controllerStateRaw.shedBuildings || [])],
    _restartHold: [...shedding.hold],
    _lastRestartAt: shedding.lastRestartAt,
    _autoIdleIds: [...autoBalance.idle],
    _autoControlLastAt: autoBalance.lastChange,
    activeModifiers: eventApplied.activeModifiers,
    activeEvent: eventApplied.activeEvent,
    eventTimeLeft: eventApplied.eventTimeLeft,
    lastEventSol: eventApplied.lastEventSol,
  });

  useGame.getState().updateObjectives?.();
  if (cargoJustArrived) useGame.getState()._completeCargo?.();
  if (crossedSol) useGame.getState().save?.("auto");
}

function doTick() {
  const now = typeof performance !== "undefined" ? performance.now() : Date.now();
  const dt = Math.min(0.1, (now - _lastTick) / 1000);
  _lastTick = now;
  tick(dt);
}

export function startGameLoop() {
  installUnifiedSystems();
  if (typeof window === "undefined" || typeof document === "undefined") return () => {};

  try {
    const s = useGame.getState();
    const hasAutosave = localStorage.getItem(BASE_AUTOSAVE_KEY);
    const hasManualSave = localStorage.getItem("mars_em2c_save_v1");
    if ((hasAutosave || hasManualSave) && s.buildings.length <= 1) {
      _baseLoad?.(Boolean(hasAutosave));
      useGame.setState({ resources: ensureExtendedResources(useGame.getState().resources) });
      restoreSystemState(false);
    }
  } catch (e) {
    console.warn("[MarsColony] Auto-load skipped", e);
  }

  const loop = () => {
    doTick();
    _rafId = requestAnimationFrame(loop);
  };

  _lastTick = performance.now();
  if (!_rafId) _rafId = requestAnimationFrame(loop);

  if (!_visibilityHandlerInstalled) {
    const onVisibilityChange = () => {
      if (document.hidden) {
        if (_rafId) { cancelAnimationFrame(_rafId); _rafId = null; }
        if (!_bgIntervalId) _bgIntervalId = setInterval(doTick, 1000);
      } else {
        if (_bgIntervalId) { clearInterval(_bgIntervalId); _bgIntervalId = null; }
        _lastTick = performance.now();
        if (!_rafId) _rafId = requestAnimationFrame(loop);
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    _visibilityHandlerInstalled = true;
  }

  if (!_autosaveTimer) {
    let lastSol = useGame.getState().sol;
    _autosaveTimer = setInterval(() => {
      const s = useGame.getState();
      if (s.sol !== lastSol) {
        lastSol = s.sol;
        s.save?.("auto");
      }
    }, 2000);
  }

  return () => {
    if (_rafId) { cancelAnimationFrame(_rafId); _rafId = null; }
    if (_bgIntervalId) { clearInterval(_bgIntervalId); _bgIntervalId = null; }
  };
}

installUnifiedSystems();
